import React, { useState, useEffect } from 'react';
import { Plus, Search, CheckCircle, XCircle, Clock, Edit, X } from 'lucide-react';

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: 1,
    appointment_date: '',
    appointment_time: '',
    notes: '',
    direction: 'maestro',
    location: 'tibbiyot_dunyosi',
    created_by: 2
  });
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    fetchAppointments();
    fetchPatients();
  }, []);

  const fetchAppointments = () => {
    fetch('/api/appointments')
      .then(res => res.json())
      .then(data => setAppointments(data));
  };

  const fetchPatients = () => {
    fetch('/api/patients')
      .then(res => res.json())
      .then(data => setPatients(data));
  };

  const updateStatus = (id, status) => {
    fetch(`/api/appointments/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    }).then(() => fetchAppointments());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const url = editingAppointment
      ? `/api/appointments/${editingAppointment.id}`
      : '/api/appointments';
    const method = editingAppointment ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
    .then(res => {
      if (!res.ok) return res.json().then(err => { throw new Error(err.error || 'Ошибка сервера'); });
      return res.json();
    })
    .then(() => {
      fetchAppointments();
      handleCancel();
    })
    .catch(error => alert('Ошибка: ' + error.message));
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      patient_id: appointment.patient_id.toString(),
      doctor_id: appointment.doctor_id,
      appointment_date: appointment.appointment_date,
      appointment_time: appointment.appointment_time,
      notes: appointment.notes || '',
      direction: appointment.direction || 'maestro',
      location: appointment.location || 'tibbiyot_dunyosi',
      created_by: appointment.created_by || 2
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAppointment(null);
    setFormData({
      patient_id: '', doctor_id: 1, appointment_date: '',
      appointment_time: '', notes: '', direction: 'maestro',
      location: 'tibbiyot_dunyosi', created_by: 2
    });
  };

  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = apt.patient_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || apt.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold"><CheckCircle className="h-4 w-4" /><span>Завершено</span></span>;
      case 'cancelled':
        return <span className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold"><XCircle className="h-4 w-4" /><span>Отменено</span></span>;
      default:
        return <span className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold"><Clock className="h-4 w-4" /><span>Запланировано</span></span>;
    }
  };

  const getDirectionName = (d) => d === 'maestro' ? 'Маэстро слуха' : d === 'vertigo' ? 'Доктор Вертиго' : 'Неизвестно';
  const getDirectionColor = (d) => d === 'maestro' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
  const getLocationName = (l) => l === 'tibbiyot_dunyosi' ? 'Tibbiyot Dunyosi' : l === 'nii_otorinolaringologii' ? 'НИИ Оториноларингологии' : 'Не указано';
  const getLocationColor = (l) => l === 'tibbiyot_dunyosi' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800';

  return (
    <div className="space-y-5">
      {/* Заголовок */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Записи на приём</h1>
          <p className="text-slate-500 mt-0.5">Управление записями пациентов</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-sm transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Новая запись</span>
        </button>
      </div>

      {/* Фильтры */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Поиск по имени пациента..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:border-blue-500 transition-colors bg-white"
          >
            <option value="all">Все статусы</option>
            <option value="scheduled">Запланировано</option>
            <option value="completed">Завершено</option>
            <option value="cancelled">Отменено</option>
          </select>
        </div>
      </div>

      {/* Список записей */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-800">
            Список записей
            <span className="ml-2 text-sm font-normal text-slate-500">({filteredAppointments.length})</span>
          </h2>
        </div>

        {filteredAppointments.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Записей не найдено</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredAppointments.map((apt) => (
              <div key={apt.id} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Имя и дата */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-slate-900">{apt.patient_name}</h3>
                    <p className="text-slate-500 mt-0.5">
                      <span className="font-medium">{apt.appointment_date}</span>
                      {' '}&nbsp;в{' '}
                      <span className="font-medium">{apt.appointment_time}</span>
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getDirectionColor(apt.direction)}`}>
                        {getDirectionName(apt.direction)}
                      </span>
                      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${getLocationColor(apt.location)}`}>
                        {getLocationName(apt.location)}
                      </span>
                    </div>
                  </div>

                  {/* Статус и кнопки */}
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    {getStatusBadge(apt.status)}
                    <button
                      onClick={() => handleEdit(apt)}
                      className="flex items-center space-x-1.5 px-3 py-2 bg-slate-100 hover:bg-blue-50 text-slate-600 hover:text-blue-700 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Edit className="h-4 w-4" />
                      <span>Изменить</span>
                    </button>
                    {apt.status === 'scheduled' && (
                      <>
                        <button
                          onClick={() => updateStatus(apt.id, 'completed')}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Завершить</span>
                        </button>
                        <button
                          onClick={() => updateStatus(apt.id, 'cancelled')}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          <XCircle className="h-4 w-4" />
                          <span>Отменить</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-y-auto max-h-[92vh] shadow-2xl">
            <div className="bg-blue-800 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {editingAppointment ? 'Редактировать запись' : 'Новая запись'}
              </h3>
              <button onClick={handleCancel} className="text-blue-300 hover:text-white p-1 rounded-lg transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Пациент *</label>
                <select
                  value={formData.patient_id}
                  onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                  required
                >
                  <option value="">Выберите пациента...</option>
                  {patients.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-semibold text-slate-700 mb-2">Дата *</label>
                  <input
                    type="date"
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-base font-semibold text-slate-700 mb-2">Время *</label>
                  <input
                    type="time"
                    value={formData.appointment_time}
                    onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Направление *</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'maestro', label: 'Маэстро слуха', color: 'border-blue-500 bg-blue-50' },
                    { value: 'vertigo', label: 'Доктор Вертиго', color: 'border-green-500 bg-green-50' }
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-colors ${formData.direction === opt.value ? opt.color : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="direction"
                        value={opt.value}
                        checked={formData.direction === opt.value}
                        onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                        className="mr-2.5 h-4 w-4"
                      />
                      <span className="text-sm font-semibold text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Место приёма *</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'tibbiyot_dunyosi', label: 'Tibbiyot Dunyosi', color: 'border-purple-500 bg-purple-50' },
                    { value: 'nii_otorinolaringologii', label: 'НИИ Оториноларингологии', color: 'border-orange-500 bg-orange-50' }
                  ].map(opt => (
                    <label key={opt.value} className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-colors ${formData.location === opt.value ? opt.color : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="location"
                        value={opt.value}
                        checked={formData.location === opt.value}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="mr-2.5 h-4 w-4"
                      />
                      <span className="text-sm font-semibold text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-slate-700 mb-2">Заметки</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  rows="3"
                  placeholder="Дополнительная информация..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 py-3.5 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-sm"
                >
                  {editingAppointment ? 'Сохранить изменения' : 'Добавить запись'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
